import * as THREE from 'three';
import Walls from '../Walls';
import MaterialUtils from '../../helpers/MaterialUtils';

export default class UStair {
    constructor(scene, model, mainSceneSetup) {
        this.scene = scene;
        this.model = model;
        this.mainSceneSetup = mainSceneSetup;
        this.treadGroup = new THREE.Group();
        this.scene.add(this.treadGroup);

        // Initialize walls
        this.walls = new Walls(this.scene, this.createWallMaterial());

        // Get initial values from sliders
        const widthSlider = document.getElementById('widthSlider');
        this.treadWidth = widthSlider ? parseInt(widthSlider.value, 10) : 1450;

        const bottomFlightSlider = document.getElementById('bottom-flight-slider');
        const bottomFlightValue = bottomFlightSlider && bottomFlightSlider.value !== '' ? bottomFlightSlider.value : null;
        this.bottomFlightTreads = !isNaN(parseInt(bottomFlightValue, 10)) ? parseInt(bottomFlightValue, 10) : 8;

        const topFlightSlider = document.getElementById('top-flight-slider');
        const topFlightValue = topFlightSlider && topFlightSlider.value !== '' ? topFlightSlider.value : null;
        this.topFlightTreads = !isNaN(parseInt(topFlightValue, 10)) ? parseInt(topFlightValue, 10) : 8;



        // Initialize numTreads right in the constructor
        this.numTreads = this.bottomFlightTreads + this.topFlightTreads;
        console.log('UStair numTreads initialized to:', this.numTreads);

        this.bottomFlightGroup = null;
        this.topFlightGroup = null;
        this.boundingBox = null;

        // Create the U-shaped staircase with default values
        //this.createStaircase(this.bottomFlightTreads, this.topFlightTreads);
        
        // Update MainSceneSetup with initial values
        this.mainSceneSetup.setFlightTreads(this.bottomFlightTreads, this.topFlightTreads);
        this.initialize();
        
        // Add event listeners for the sliders
        if (this.bottomFlightSlider) {
            this.bottomFlightSlider.addEventListener('input', (event) => {
                this.bottomFlightTreads = parseInt(event.target.value, 10);
                this.updateStairTreads(this.bottomFlightTreads, this.topFlightTreads);
                this.mainSceneSetup.setFlightTreads(this.bottomFlightTreads, this.topFlightTreads);
            });
        }
        
        if (this.topFlightSlider) {
            this.topFlightSlider.addEventListener('input', (event) => {
                this.topFlightTreads = parseInt(event.target.value, 10);
                this.updateStairTreads(this.bottomFlightTreads, this.topFlightTreads);
                this.mainSceneSetup.setFlightTreads(this.bottomFlightTreads, this.topFlightTreads);
            });
        }
    }
    async initialize() {
        this.createStaircase(this.bottomFlightTreads, this.topFlightTreads);
    
        // Ensure that the bounding box and treadGroup are fully initialized
        const boundingBox = new THREE.Box3().setFromObject(this.treadGroup);
        if (!boundingBox.isEmpty()) {
            this.isInitialized = true;
        } else {
            console.error('Bounding box not properly initialized');
        }
        return Promise.resolve();
    }

    
    // Function to create the U-shaped staircase with dynamic back wall movement
    createStaircase(bottomTreads, topTreads) {
        if (isNaN(bottomTreads) || bottomTreads <= 0) {
            console.warn('Invalid bottomTreads value. Defaulting to 8.');
            bottomTreads = 8;
        }
        if (isNaN(topTreads) || topTreads <= 0) {
            console.warn('Invalid topTreads value. Defaulting to 8.');
            topTreads = 8;
        }
        this.bottomFlightTreads = bottomTreads;
        this.topFlightTreads = topTreads;
        this.numTreads = topTreads + bottomTreads;
        console.log('UStair Updated numTreads:', this.numTreads);
        console.log(`Creating U-shaped staircase with ${bottomTreads} bottom treads and ${topTreads} top treads`);
        this.clearTreads();
        this.mainSceneSetup.setFlightTreads(bottomTreads, topTreads);
        const distance = 1.0;  // Distance between each tread
        const landingDepth = (this.treadWidth / 800) * 1.5;  // Landing depth proportional to the tread width
        const landingWidth = (this.treadWidth / 800) * 3;
        const sideWallThickness = 3;

        let topTreadYOffset = 0;
        let topTreadZOffset = 0;

        // --- Bottom Flight ---
        const bottomFlightGroup = new THREE.Group();  // Create a group for the bottom flight
        for (let i = 0; i < bottomTreads; i++) {
            const newTread = this.model.clone();
            newTread.scale.set(this.treadWidth / 900, 1, 1);
            const angle = THREE.MathUtils.degToRad(125);
            const yOffset = distance * i * Math.sin(angle) * 0.5;
            const zOffset = -distance * i * Math.cos(angle);

            newTread.position.set(0, yOffset, zOffset);
            newTread.castShadow = true;
            newTread.receiveShadow = true;
            bottomFlightGroup.add(newTread);  // Add each tread to the bottom flight group

            if (i === bottomTreads - 1) {
                topTreadYOffset = yOffset;
                topTreadZOffset = zOffset;  // Track the last bottom tread's Z-offset
            }
        }
        this.treadGroup.add(bottomFlightGroup);  // Add bottom flight group to the main group

        // Calculate bounding box for the bottom flight
        const bottomFlightBoundingBox = new THREE.Box3().setFromObject(bottomFlightGroup);
        const bottomFlightBoxSize = bottomFlightBoundingBox.getSize(new THREE.Vector3());
        console.log('Bottom Flight Bounding Box:', bottomFlightBoundingBox);
        //const bottomFlightHelper = new THREE.Box3Helper(bottomFlightBoundingBox, 0xffbaa3);
        //this.scene.add(bottomFlightHelper);  // Add the helper to the scene

        // --- Landing ---
        const landing = new THREE.Mesh(
            new THREE.BoxGeometry(landingWidth, 0.2, landingDepth),
            MaterialUtils.createWoodMaterial()
        );
        landing.position.set(landingWidth / 4, topTreadYOffset + 0.5, topTreadZOffset + landingDepth / 2);
        landing.castShadow = true;
        landing.receiveShadow = true;
        this.treadGroup.add(landing);

        // --- Top Flight ---
        const topFlightGroup = new THREE.Group();  // Create a group for the top flight
        for (let i = 0; i < topTreads; i++) {
            const newTread = this.model.clone();
            newTread.scale.set(this.treadWidth / 800, 1, 1);
            const yOffset = distance * (i + 1) * Math.sin(THREE.MathUtils.degToRad(125)) * 0.5;
            const zOffset = distance * (i + 1) * Math.cos(THREE.MathUtils.degToRad(125));
            const xOffset = this.treadWidth / 1600 + 1.2;

            newTread.position.set(
                xOffset, 
                topTreadYOffset + yOffset + 0.3,
                topTreadZOffset + zOffset
            );
            newTread.rotation.y = THREE.MathUtils.degToRad(180);
            newTread.castShadow = true;
            newTread.receiveShadow = true;
            topFlightGroup.add(newTread);  // Add each tread to the top flight group

            if (i === topTreads - 1) {
                topTreadYOffset += yOffset;
                topTreadZOffset += zOffset;
            }
        }
        this.treadGroup.add(topFlightGroup);  // Add top flight group to the main group
        this.bottomFlightGroup = bottomFlightGroup;
        this.topFlightGroup = topFlightGroup;
        // Calculate bounding box for the top flight
        const topFlightBoundingBox = new THREE.Box3().setFromObject(topFlightGroup);
        const topFlightBoxSize = topFlightBoundingBox.getSize(new THREE.Vector3());
        //const topFlightHelper = new THREE.Box3Helper(topFlightBoundingBox, 0xffbdfe);  // Different color for top flight
        //this.scene.add(topFlightHelper);  // Add the helper to the scene
        if (!topFlightBoxSize || isNaN(topFlightBoxSize.x) || isNaN(topFlightBoxSize.y) || isNaN(topFlightBoxSize.z)) {
            console.error('Invalid topFlightBoxSize:', topFlightBoxSize);
            return;
        }
        // Update the bounding box for the entire staircase
        this.calculateBoundingBox(topFlightBoundingBox, topFlightBoxSize);
    }

    // Function to calculate and update the bounding box for the entire staircase
    calculateBoundingBox(topFlightBoundingBox, topFlightBoxSize) {
        if (this.treadGroup.children.length === 0) {
            console.error('treadGroup is empty. Cannot calculate bounding box.');
            return;
        }
        this.treadGroup.add(this.bottomFlightGroup);
        this.treadGroup.add(this.topFlightGroup);
        const boundingBox = new THREE.Box3();
        boundingBox.setFromObject(this.treadGroup); // Calculate bounding box based on all treads
        const boxSize = boundingBox.getSize(new THREE.Vector3());

        if (!boxSize || isNaN(boxSize.x) || isNaN(boxSize.y) || isNaN(boxSize.z)) {
            console.error('Invalid bounding box size:', boxSize);
            return;
        }
        if (this.isInitialized) {
        // Update the walls based on the bounding box dimensions
        this.updateWallsWithBoundingBox(boundingBox, boxSize, topFlightBoundingBox, topFlightBoxSize);
        }
        // Optional: Visualize the bounding box
        //const helper = new THREE.Box3Helper(boundingBox, 0xbdffee);
        //this.scene.add(helper);

        console.log('Bounding Box:', boundingBox);
    }

    // Update walls based on bounding box for entire staircase and Z position of back wall based on top flight
    updateWallsWithBoundingBox(boundingBox, boxSize, topFlightBoundingBox, topFlightBoxSize) {
        const { x: boxWidth, y: boxHeight, z: boxDepth } = boxSize || {};
    
        // Check if boxSize properties are valid
        if (!boxWidth || !boxHeight || !boxDepth || !isFinite(boxWidth) || !isFinite(boxHeight) || !isFinite(boxDepth)) {
            console.error('Invalid or undefined box size in updateWallsWithBoundingBox:', boxSize);
            return; // Exit early if bounding box calculations are invalid
        }
    
        const wallHeight = boxHeight;  // Adjust height relative to bounding box
        const wallFloorWidth = 3;          // Pathway width for the top of the walls
        const backWallHeight = 1.2;        // Height of the back wall
    
        // Update back wall geometry and position based on the top flight bounding box
        this.walls.backWall.geometry = new THREE.BoxGeometry(boxWidth + (wallFloorWidth * 2), backWallHeight, wallFloorWidth);
        this.walls.backWall.position.set(
            boundingBox.min.x + boxWidth / 2,  // X position based on the entire staircase bounding box
            boundingBox.max.y - backWallHeight / 2,  // Y position aligns with the top of the entire staircase
            topFlightBoundingBox.min.z - (wallFloorWidth / 2) + 0.2  // Z position behind the entire top flight
        );
    
        // Update side wall geometry
        this.walls.sideWall.geometry = new THREE.BoxGeometry(wallFloorWidth, wallHeight, boxDepth + 1);
    
        // Position the side wall relative to the bounding box
        this.walls.sideWall.position.set(
            boundingBox.min.x - wallFloorWidth / 2,  // Move side wall to the left of the bounding box
            boundingBox.min.y + wallHeight / 2,      // Y position based on the height of the bounding box
            boundingBox.min.z + boxDepth / 2         // Z position centered along the bounding box
        );

        const isCantilever = document.querySelector('input[name="cantilever-option"]:checked').value === 'cantilever';
        const isCentralStringer = document.querySelector('input[name="cantilever-option"]:checked').value === 'central-stringer';
        // Configure cantileverWall for cantilever mode
        if (isCantilever) {
            this.walls.cantileverWall.visible = true;

            // Set cantilever wall geometry and position based on bounding box dimensions
            this.walls.cantileverWall.geometry = new THREE.BoxGeometry(0.01, boxHeight, boxDepth); // One-sided wall using height and depth

            // Position the cantilever wall along the back edge, centered on y and z
            this.walls.cantileverWall.position.set(
                boundingBox.max.x, // Slight offset from the edge
                boundingBox.max.y - boxHeight / 2,  // Centered along the y-axis
                boundingBox.max.z - boxDepth / 2    // Centered along the z-axis
            );

            console.log('Cantilever wall position:', this.walls.cantileverWall.position);
        } 
        
        else if (isCentralStringer){
            this.walls.cantileverWall.visible = false;
        } else {
            this.walls.cantileverWall.visible = false; // Hide the wall when cantilever is not active
        }
    }
    

    // Function to update stair treads (triggered by sliders)
    updateStairTreads(bottomTreads, topTreads) {
        this.bottomFlightTreads = bottomTreads;
        this.topFlightTreads = topTreads;
        this.numTreads = topTreads + bottomTreads;
        console.log('UStair Updated numTreads:', this.numTreads);
        // Update MainSceneSetup before recreating the staircase
        this.mainSceneSetup.setFlightTreads(bottomTreads, topTreads);
        // Recreate the staircase with updated values
        this.createStaircase(this.bottomFlightTreads, this.topFlightTreads);
    }

    // Function to update the tread width
    updateTreadWidth(width) {
        this.treadWidth = width;
        this.createStaircase(this.bottomFlightTreads, this.topFlightTreads);
    }

    // Function to update material (e.g., based on user input)
    updateMaterial(selectedMaterial) {
        this.treadGroup.children.forEach(tread => {
            MaterialUtils.updateTreadMaterial(selectedMaterial, tread);
        });
    }

    // Function to clear existing treads
    clearTreads() {
        while (this.treadGroup.children.length > 0) {
            const tread = this.treadGroup.children.pop();
            this.scene.remove(tread);
        }
    }

    // Function to create wall material
    createWallMaterial() {
        const textureLoader = new THREE.TextureLoader();
        const wallTexture = textureLoader.load('../../textures/cross-laminated-timber-clt-fir-2000-mm-architextures.jpg');
        wallTexture.wrapS = THREE.RepeatWrapping;
        wallTexture.wrapT = THREE.RepeatWrapping;
        return new THREE.MeshStandardMaterial({ map: wallTexture });
    }
}
